import { Controller } from "@hotwired/stimulus"
import Shepherd from 'shepherd.js'

export default class extends Controller {
  connect() {
    this.initializeTour()
  }

  disconnect() {
    if (this.tour) {
      this.tour.destroy()
    }
  }

  initializeTour() {
    // Prüfe ob der User bereits die Tour gesehen hat
    if (this.hasSeenTour()) {
      return
    }

    this.tour = new Shepherd.Tour({
      defaultStepOptions: {
        cancelIcon: {
          enabled: true
        },
        classes: 'shadow-md bg-purple-dark',
        scrollTo: true
      },
      useModalOverlay: true
    })

    this.addTourSteps()
    this.tour.start()
  }

  addTourSteps() {
    // Willkommens-Schritt
    this.tour.addStep({
      id: 'welcome',
      title: '🎯 Willkommen bei AtlasTitan Recruiting v2!',
      text: 'Lassen Sie uns Ihr neues, modernes Dashboard erkunden. Hier finden Sie alles, was Sie für erfolgreiches Recruiting brauchen.',
      buttons: [
        { text: 'Später', action: this.tour.cancel },
        { text: 'Los geht\'s!', action: this.tour.next }
      ]
    })

    // Hero Section
    this.tour.addStep({
      id: 'hero-section',
      title: '🚀 Ihr persönlicher Recruiting-Überblick',
      text: 'Hier sehen Sie auf einen Blick Ihre wichtigsten Kennzahlen: Bewerber, Jobs, Requisitions und erfolgreiche Placements.',
      attachTo: { element: '.hero-section', on: 'bottom' },
      buttons: [
        { text: 'Zurück', action: this.tour.back },
        { text: 'Weiter', action: this.tour.next }
      ]
    })

    // KPI Grid
    this.tour.addStep({
      id: 'kpi-grid',
      title: '📊 Ihre Recruiting-KPIs',
      text: 'Diese Karten zeigen Ihnen die wichtigsten Metriken für Ihren Recruiting-Erfolg. Alle Werte werden in Echtzeit aktualisiert.',
      attachTo: { element: '.kpi-grid', on: 'top' },
      buttons: [
        { text: 'Zurück', action: this.tour.back },
        { text: 'Weiter', action: this.tour.next }
      ]
    })

    // AI Insights
    this.tour.addStep({
      id: 'ai-insights',
      title: '🧠 KI-gestützte Insights',
      text: 'Unser System analysiert kontinuierlich Ihre Daten und gibt Ihnen wertvolle Einblicke in Skill-Trends und Marktentwicklungen.',
      attachTo: { element: '.ai-insights-section', on: 'top' },
      buttons: [
        { text: 'Zurück', action: this.tour.back },
        { text: 'Weiter', action: this.tour.next }
      ]
    })

    // Priority Actions
    this.tour.addStep({
      id: 'priority-actions',
      title: '⚡ Prioritäten heute',
      text: 'Hier sehen Sie, was heute am wichtigsten ist: dringende Bewerber, offene Positionen und anstehende Interviews.',
      attachTo: { element: '.priority-actions-section', on: 'top' },
      buttons: [
        { text: 'Zurück', action: this.tour.back },
        { text: 'Weiter', action: this.tour.next }
      ]
    })

    // Market Trends
    this.tour.addStep({
      id: 'market-trends',
      title: '📈 Markt-Trends & Wettbewerb',
      text: 'Bleiben Sie über Gehaltsentwicklungen, Standort-Trends und Team-Performance informiert.',
      attachTo: { element: '.market-trends-section', on: 'top' },
      buttons: [
        { text: 'Zurück', action: this.tour.back },
        { text: 'Weiter', action: this.tour.next }
      ]
    })

    // Recent Activities
    this.tour.addStep({
      id: 'recent-activities',
      title: '🔄 Letzte Aktivitäten',
      text: 'Hier sehen Sie alle wichtigen Änderungen und Aktivitäten in Ihrem Recruiting-System in Echtzeit.',
      attachTo: { element: '.recent-activities-section', on: 'top' },
      buttons: [
        { text: 'Zurück', action: this.tour.back },
        { text: 'Weiter', action: this.tour.next }
      ]
    })

    // Quick Actions
    this.tour.addStep({
      id: 'quick-actions',
      title: '🚀 Schnellzugriff',
      text: 'Direkte Links zu allen wichtigen Bereichen: Bewerber, Jobs, Requisitions und Termine.',
      attachTo: { element: '.quick-actions-footer', on: 'top' },
      buttons: [
        { text: 'Zurück', action: this.tour.back },
        { text: 'Verstanden!', action: this.tour.complete }
      ]
    })

    // Tour abgeschlossen
    this.tour.on('complete', () => {
      this.markTourAsSeen()
      this.showCompletionMessage()
    })

    this.tour.on('cancel', () => {
      this.showCompletionMessage()
    })
  }

  hasSeenTour() {
    return localStorage.getItem('atlastitan_dashboard_v2_tour_completed') === 'true'
  }

  markTourAsSeen() {
    localStorage.setItem('atlastitan_dashboard_v2_tour_completed', 'true')
  }

  showCompletionMessage() {
    // Zeige eine Erfolgsmeldung
    const message = document.createElement('div')
    message.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50'
    message.innerHTML = `
      <div class="flex items-center space-x-2">
        <span>🎉 Dashboard v2 Tour abgeschlossen!</span>
        <button onclick="this.parentElement.parentElement.remove()" class="ml-2 text-white hover:text-gray-200">×</button>
      </div>
    `
    document.body.appendChild(message)

    // Automatisch nach 5 Sekunden ausblenden
    setTimeout(() => {
      if (message.parentElement) {
        message.remove()
      }
    }, 5000)
  }

  // Methode um die Tour manuell zu starten
  startTour() {
    if (this.tour) {
      this.tour.destroy()
    }
    this.initializeTour()
  }

  // Methode um die Tour zurückzusetzen
  resetTour() {
    localStorage.removeItem('atlastitan_dashboard_v2_tour_completed')
    this.startTour()
  }
}
